import React, { useId, useState } from 'react';
import clsx from 'clsx';
import IconDragDropVertical from '@/assets/icons/icon-drag-drop-vertical.svg?react';
import IconMail from '@/assets/icons/icon-mail.svg?react';
import { SecondaryButton } from '@/components/buttons/SecondaryButton/SecondaryButton';
import IconTextSquare from '@/assets/icons/icon-text-square.svg?react';
import IconCall from '@/assets/icons/icon-call.svg?react';
import IconCheckmarkSquare from '@/assets/icons/icon-checkmark-square.svg?react';
import IconLeftToRightListBullet from '@/assets/icons/icon-left-to-right-list-bullet.svg?react';
import IconCheckList from '@/assets/icons/icon-check-list.svg?react';
import IconCopy from '@/assets/icons/icon-copy.svg?react';
import IconDelete from '@/assets/icons/icon-delete.svg?react';
import IconSquareLock from '@/assets/icons/icon-square-lock.svg?react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import type { FormFieldType } from '@builder4/shared/types/form';

export interface FormFieldProps {
  id?: string;
  className?: string;
  label: string;
  selected: boolean;
  draggable: boolean;
  fieldType: FormFieldType;
  isPrimaryEmailField?: boolean;
  onFieldTypeChange: (type: FormFieldType) => void;
  onEdit: (id: string) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  onDragStart?: (id: string) => void;
  onDragEnd?: (id: string) => void;
  onDropOver?: (sourceId: string, targetId: string) => void;
  onDragOverItem?: (targetId: string) => void;
  onDragLeaveItem?: (targetId: string) => void;
}

export const FormField: React.FC<FormFieldProps> = ({
  id,
  className = '',
  label = 'Email',
  selected = false,
  draggable = true,
  fieldType = 'email',
  isPrimaryEmailField = false,
  onFieldTypeChange,
  onDragStart,
  onDragEnd,
  onDropOver,
  onDragOverItem,
  onDragLeaveItem,
  onEdit,
  onDuplicate,
  onDelete,
}) => {
  const autoId = useId();
  const fieldId = id ?? `form-field-${autoId}`;
  const [dragging, setDragging] = useState(false);
  const [canDrag, setCanDrag] = useState(false);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    if (!draggable) return;
    if (!canDrag) {
      e.preventDefault();
      return;
    }
    setDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData('text/plain', fieldId);
      const img = document.createElement('canvas');
      img.width = 1;
      img.height = 1;
      e.dataTransfer.setDragImage(img, 0, 0);
    } catch {
      // ignore drag image errors in unsupported environments
    }
    onDragStart?.(fieldId);
  };

  const handleDragEnd = () => {
    setDragging(false);
    setCanDrag(false);
    onDragEnd?.(fieldId);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    onDragOverItem?.(fieldId);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    onDragOverItem?.(fieldId);
  };

  const handleDragLeave = () => {
    onDragLeaveItem?.(fieldId);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const sourceId = e.dataTransfer.getData('text/plain');
    if (sourceId && sourceId !== fieldId) {
      onDropOver?.(sourceId, fieldId);
    }
  };

  const baseClasses =
    'inline-flex h-9 w-full select-none items-center justify-start gap-2 rounded-md pr-1 transition-all duration-150 ease-out will-change-transform';
  const hoverClasses =
    selected || dragging ? 'bg-white shadow-[0px_0px_8px_0px_rgba(0,0,0,0.10)]' : '';

  type IconType = React.ComponentType<React.SVGProps<SVGSVGElement>>;
  const typeToIcon: Record<NonNullable<FormFieldProps['fieldType']>, IconType> = {
    text: IconTextSquare,
    email: IconMail,
    phone: IconCall,
    consent: IconCheckmarkSquare,
    dropdown: IconLeftToRightListBullet,
    selection: IconCheckList,
  };
  const typeToLabel: Record<NonNullable<FormFieldProps['fieldType']>, string> = {
    text: 'Text',
    email: 'Email',
    phone: 'Phone',
    consent: 'Consent',
    dropdown: 'Dropdown',
    selection: 'Selection',
  };
  const CurrentIcon = typeToIcon[fieldType];
  const shownLabel = label && label !== 'Field Title' ? label : typeToLabel[fieldType];

  const fieldTypeOptions = Object.entries(typeToLabel).map(([value, label]) => ({
    value: value as NonNullable<FormFieldProps['fieldType']>,
    label,
  }));

  return (
    <div
      id={fieldId}
      className={clsx(baseClasses, hoverClasses, className)}
      draggable={draggable}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      aria-grabbed={dragging}
    >
      <div
        className="option-drag-handle grid h-5 w-5 cursor-grab place-items-center"
        onMouseDown={() => setCanDrag(true)}
        onMouseUp={() => setCanDrag(false)}
        onMouseLeave={() => setCanDrag(false)}
        data-drag-handle
      >
        <IconDragDropVertical className="h-4 w-4 text-neutral-500" />
      </div>

      {isPrimaryEmailField ? (
        <div className="relative flex h-8 w-36 flex-none items-center rounded-md bg-neutral-50">
          <div className="flex w-full items-center gap-1 px-2 pr-8">
            <CurrentIcon className="h-4 w-4 flex-shrink-0 text-neutral-500" />
            <span className="text-button-sm flex-1 truncate text-left font-medium text-neutral-900">
              {shownLabel}
            </span>
          </div>
          <IconSquareLock className="absolute right-2 h-4 w-4 text-neutral-500" />
        </div>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div className="relative flex h-8 w-36 flex-none cursor-pointer items-center rounded-md bg-neutral-50 transition-colors hover:bg-neutral-100">
              <div className="flex w-full items-center gap-1 px-2 pr-8">
                <CurrentIcon className="h-4 w-4 flex-shrink-0 text-neutral-500" />
                <span className="text-button-sm flex-1 truncate text-left font-medium text-neutral-900">
                  {shownLabel}
                </span>
              </div>
              <svg
                className="absolute right-2 h-4 w-4 text-neutral-500"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M4 6L8 10L12 6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            className="bg-neutral-0 w-36 rounded-md p-1 shadow-[0px_0px_8px_0px_rgba(0,0,0,0.10)] outline-1 outline-offset-[-1px] outline-neutral-100"
            sideOffset={6}
            side="bottom"
            align="start"
          >
            {fieldTypeOptions.map((option) => (
              <DropdownMenuItem
                key={option.value}
                className={clsx(
                  'flex h-8 cursor-pointer items-center justify-between rounded-lg px-2',
                  fieldType === option.value ? 'bg-neutral-0' : 'hover:bg-neutral-50',
                )}
                onSelect={() => onFieldTypeChange?.(option.value)}
              >
                <div
                  className={clsx(
                    'text-xs leading-4 font-medium',
                    fieldType === option.value ? 'text-indigo-500' : 'text-neutral-900',
                  )}
                >
                  {option.label}
                </div>
                {fieldType === option.value && (
                  <svg
                    className="h-4 w-4 text-indigo-500"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M13.3334 4L6.00002 11.3333L2.66669 8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </DropdownMenuItem>
            ))}

            <DropdownMenuSeparator className="my-1 h-0 border-t border-neutral-100" />

            <DropdownMenuItem
              className="flex h-8 cursor-pointer items-center gap-1 rounded-lg px-2 hover:bg-neutral-50"
              onSelect={() => onDuplicate?.(fieldId)}
            >
              <IconCopy className="h-4 w-4 text-neutral-900" />
              <div className="text-xs leading-4 font-medium text-neutral-900">Duplicate</div>
            </DropdownMenuItem>

            <DropdownMenuItem
              className="flex h-8 cursor-pointer items-center gap-1 rounded-lg px-2 hover:bg-neutral-50"
              onSelect={() => onDelete?.(fieldId)}
            >
              <IconDelete className="text-coral-500 h-4 w-4" />
              <div className="text-coral-500 text-xs leading-4 font-medium">Delete field</div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <SecondaryButton
        size="sm"
        state="default"
        text="Edit"
        icon={null}
        className="ml-auto h-7 px-3"
        onClick={() => onEdit?.(fieldId)}
      />
    </div>
  );
};

export default FormField;
